import React, { useState } from 'react';
import { X, CheckCircle2 } from 'lucide-react';
import { CitationLink, CitationPopover } from './Citations';
import { BRAND } from '../kit/tokens';

// ============================================================================
// LoanTypeSelector — pick which agency guideline drives self-employed math
// ============================================================================
export function LoanTypeSelector({ loanType, setLoanType, rules, currentRules }) {
  const LOAN_TYPE_STYLE = {
    conventional: { color: BRAND.navy, bg: '#EEF2FF', short: 'CONV' },
    fannie:       { color: '#065F46', bg: '#ECFDF5', short: 'FNMA' },
    freddie:      { color: '#1E40AF', bg: '#EFF6FF', short: 'FHLMC' },
    fha:          { color: '#9A3412', bg: '#FFF7ED', short: 'FHA' },
    va:           { color: '#7C2D12', bg: '#FEF3C7', short: 'VA' },
    usda:         { color: '#166534', bg: '#F0FDF4', short: 'USDA' },
  };
  const orderedKeys = ['conventional', 'fannie', 'freddie', 'fha', 'va', 'usda'];
  return (
    <div className="rounded-2xl border border-slate-200 shadow-sm bg-white overflow-hidden" data-testid="loan-type-selector">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-brand-accent">Agency Guideline</div>
          <div className="font-serif text-base font-semibold text-brand-navy">
            Applies to self-employed & rental analysis
          </div>
        </div>
        {currentRules && (
          <div className="text-[10px] uppercase tracking-wider text-slate-400">
            {currentRules.handbook_reference}
          </div>
        )}
      </div>
      <div className="p-3 grid grid-cols-3 md:grid-cols-6 gap-2">
        {orderedKeys.map((k) => {
          const isActive = loanType === k;
          const s = LOAN_TYPE_STYLE[k];
          const r = rules.find((x) => x.key === k);
          return (
            <button
              key={k}
              onClick={() => setLoanType(k)}
              data-testid={`loan-type-${k}`}
              className="p-2 rounded-lg text-center transition"
              style={isActive
                ? { border: `2px solid ${s.color}`, background: s.bg }
                : { border: '1px solid #e2e8f0', background: '#fff' }}
            >
              <div className="text-[10px] font-bold" style={{ color: isActive ? s.color : '#64748b' }}>{s.short}</div>
              <div className="text-[10px] mt-0.5" style={{ color: isActive ? s.color : '#94a3b8' }}>
                {r ? r.display_name.replace(/\s*\(.*\)$/, '').replace(/^Conventional — /, '') : k}
              </div>
            </button>
          );
        })}
      </div>
      {currentRules && (
        <div className="px-5 pb-4 pt-1">
          <div className="rounded-lg p-3 text-xs" style={{ background: '#F8FAFC' }}>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-600">
              <AddBackChip label="Depreciation" allow={currentRules.add_backs.depreciation} topic="depreciation_addback" agency={loanType} />
              <AddBackChip label="Depletion" allow={currentRules.add_backs.depletion} topic="depreciation_addback" agency={loanType} />
              <AddBackChip label="Amort/Casualty" allow={currentRules.add_backs.amortization_casualty} topic="depreciation_addback" agency={loanType} />
              <AddBackChip label="Business Use of Home" allow={currentRules.add_backs.business_use_of_home} topic="business_use_of_home" agency={loanType} />
              <AddBackChip label="Meals 50%" allow={currentRules.add_backs.meals_50pct} topic="meals_50pct" agency={loanType} />
              <AddBackChip label="Non-deductible T&E" allow={currentRules.add_backs.non_deductible_te} topic="non_deductible_te" agency={loanType} />
              <AddBackChip label="Mileage" allow={currentRules.add_backs.mileage} topic="depreciation_addback" agency={loanType} />
            </div>
            <div className="mt-2 text-[11px] text-slate-500 flex items-center justify-between">
              <div>
                <b>Averaging:</b> {currentRules.averaging.default_months} mo default • {currentRules.averaging.declining_income_rule.replace(/_/g, ' ')}
                {currentRules.requires_ytd_pl && <> • <b>Requires YTD P&L</b></>}
              </div>
              <CitationLink topic="self_employment_documentation" agency={loanType} label="View guidelines" />
            </div>
            {currentRules.notes && currentRules.notes.length > 0 && (
              <ul className="mt-2 text-[11px] text-slate-500 space-y-0.5 list-disc pl-4">
                {currentRules.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AddBackChip({ label, allow, topic, agency }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex items-center gap-1 text-[11px]">
      {allow
        ? <CheckCircle2 size={12} style={{ color: '#059669' }} />
        : <X size={12} style={{ color: '#94a3b8' }} />}
      <span
        onClick={topic ? () => setOpen(true) : undefined}
        style={{
          color: allow ? '#065F46' : '#94a3b8',
          textDecoration: allow ? 'none' : 'line-through',
          cursor: topic ? 'pointer' : 'default',
          borderBottom: topic ? '1px dotted currentColor' : undefined,
        }}
        title={topic ? 'Click to view guideline citation' : undefined}
      >
        {label}
      </span>
      {open && topic && (
        <CitationPopover topic={topic} agency={agency} onClose={() => setOpen(false)} />
      )}
    </span>
  );
}
